import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { StatusBadge, TreatmentBadge } from "@/components/Badges";
import { api, errorMessage, fmtDate, jobTitle } from "@/lib/api";
import { buildJobOptions, detectSource, getPrefs, rememberRights, rightsConfirmedOnce } from "@/lib/prefs";
import { deriveFurnishing } from "@/lib/stylist";
import { takePendingFiles, takePendingUrl } from "@/lib/pending";
import { useSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { SmartImage } from "@/components/SmartImage";
import { Input } from "@/components/ui/input";
import { useFreshMedia } from "@/hooks/useFreshMedia";
import { useMode } from "@/lib/mode";
import { ArrowRight, ArrowLeft, Loader2, Link2, ImagePlus, X, Sparkles, Upload, Check, Home, Building2, ShieldCheck, MapPin } from "lucide-react";

const TYPE_NB = { apartment: "Leilighet", house: "Enebolig", room: "Hybel/rom", cabin: "Hytte", townhouse: "Rekkehus", other: "Bolig" };

const Seg = ({ value, options, onChange, ariaLabel, testidPrefix, disabled }) => (
  <div className="seg flex-wrap" role="radiogroup" aria-label={ariaLabel} aria-disabled={disabled || undefined}>
    {options.map((o) => (
      <button key={o.value} type="button" role="radio" aria-checked={value === o.value} disabled={disabled} onClick={() => onChange(o.value)}
        className="seg-item disabled:opacity-50" data-testid={`${testidPrefix}-${o.value}`}>{o.label}</button>
    ))}
  </div>
);

/* a picked file before upload. Desktop browsers cannot decode HEIC, so when the preview fails we show a calm tile with the
   file name instead of a blank square - the server makes a JPEG preview the moment it is uploaded. */
const LocalPreview = ({ url, name }) => {
  const [failed, setFailed] = useState(false);
  const ext = (name?.split(".").pop() || "").toUpperCase();
  if (failed) {
    return (
      <div className="h-full w-full bg-secondary flex flex-col items-center justify-center gap-1 px-2 text-center" data-testid="upload-preview-fallback">
        <ImagePlus className="h-4 w-4 text-foreground/50" />
        <span className="text-[10.5px] font-medium text-foreground/70">{ext || "BILDE"}</span>
        <span className="w-full truncate text-[10px] text-svak">{name}</span>
      </div>
    );
  }
  return <img src={url} alt={name} onError={() => setFailed(true)} className="h-full w-full object-cover" />;
};


export default function HomePage() {
  const nav = useNavigate();
  const { paths, mode } = useMode();
  useSeo({ title: mode === "lab" ? "Lab · Ny jobb" : "Ny annonse", description: "Last opp bilder eller lim inn en annonse – MagicAd gjør resten.", noindex: true });
  const [config, setConfig] = useState(null);
  const [url, setUrl] = useState("");
  const [rights, setRights] = useState(rightsConfirmedOnce());
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(null);
  const [files, setFiles] = useState([]);
  const [running, setRunning] = useState(false);
  const [recent, setRecent] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [rentalChoice, setRentalChoice] = useState(null); // null -> derived from the source (Airbnb -> short_term)
  // the owner's framing facts - reality and intent, never design (bed size, lamps, rugs, curtains are DigiHome's call)
  const [furnishingChoice, setFurnishingChoice] = useState(undefined); // undefined -> derived from the listing text if possible
  const [stylingLevel, setStylingLevel] = useState("recommended");   // Ingen / Anbefalt (default) / Mer innbydende
  const [name, setName] = useState("");                                // optional: address or a name for the ad (uploads)
  const fileRef = useRef(null);
  const source = detectSource(url);
  const rentalType = rentalChoice || ((imported?.source || source) === "airbnb" ? "short_term" : "long_term");

  const isImage = (f) => f.type.startsWith("image/") || /\.(heic|heif|jpe?g|png|webp)$/i.test(f.name || "");
  const onFiles = useCallback((list) => setFiles((prev) => [...prev, ...Array.from(list || []).filter(isImage)]), []);

  const doImport = useCallback(async (u, opts = {}) => {
    const value = (u ?? url).trim();
    if (!value) return;
    if (!rights && !opts.fromLanding) { toast.error("Bekreft at du har rett til å bruke bildene"); return; }
    setImporting(true);
    try {
      const r = await api.post("/v1/imports", { source: detectSource(value), url: value, rights_confirmed: true, max_images: 25 });
      rememberRights();
      setRights(true);
      setImported(r.data);
      setFurnishingChoice(undefined);
      if (!r.data.images?.length) toast.warning("Fant ingen bilder på siden – last opp bildene i stedet");
      else toast.success(`Hentet ${r.data.images.length} bilder`);
    } catch (e) { toast.error(errorMessage(e)); } finally { setImporting(false); }
  }, [url, rights]);

  // arriving from the landing page: pick up the files / link the visitor already gave us - once
  useEffect(() => {
    api.get("/lab/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/v1/jobs", { params: { limit: 6 } }).then((r) => setRecent(r.data.items || [])).catch(() => {});
    const pend = takePendingFiles();
    if (pend.files.length) { onFiles(pend.files); toast.success(`${pend.files.length} bilder fra forsiden er med`); }
    else if (pend.lost) toast.warning("Bildene fra forsiden gikk tapt da siden ble lastet på nytt – last dem opp igjen her");
    const pu = takePendingUrl();
    if (pu) { setUrl(pu); doImport(pu, { fromLanding: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previews = useMemo(() => files.map((f) => ({ name: f.name, url: URL.createObjectURL(f) })), [files]);
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);
  // an import left open on screen: refresh its signed preview links when the tab comes back / a tile fails
  const refreshImport = useCallback(async () => {
    if (!imported?.import_id) return;
    const r = await api.get(`/v1/imports/${imported.import_id}`);
    setImported((cur) => (cur && cur.import_id === r.data.import_id ? { ...cur, ...r.data } : cur));
  }, [imported?.import_id]);
  useFreshMedia(refreshImport, { enabled: Boolean(imported?.import_id) });

  const run = async () => {
    if (!config) return;
    const opts = buildJobOptions(getPrefs(), config);
    setRunning(true);
    try {
      let uploadId = null;
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        const up = await api.post("/lab/uploads", fd);
        uploadId = up.data.upload_id;
        const skipped = up.data.errors || [];
        if (skipped.length) toast.warning(`${skipped.length} fil${skipped.length === 1 ? "" : "er"} ble hoppet over`, { description: skipped.map((e) => e.filename).filter(Boolean).slice(0, 3).join(", ") });
      }
      if (!uploadId && !imported?.import_id) { toast.error("Legg inn en annonse-lenke eller last opp bilder"); return; }
      const sl = imported?.source_listing || {};
      const given = name.trim() || undefined;
      const body = {
        external_property_id: imported ? `${imported.source}-${imported.source_id || imported.import_id}` : `upload-${Date.now()}`,
        import_id: imported?.import_id || undefined,
        upload_id: uploadId || undefined,
        property_context: { property_type: sl.property_type || undefined, bedrooms: sl.bedrooms ?? undefined, area_sqm: sl.area_sqm ?? undefined, address: sl.address || given },
        listing_context: imported ? { source_listing: sl } : undefined,
        requested_outputs: { optimized_gallery: true, hero_recommendation: true, retake_recommendations: true, listing_copy: true },
        language: opts.language,
        rental_type: rentalType,
        owner_choices: { furnishing: furnishing || null, styling_level: stylingLevel },
        pipeline_version: opts.version,
        options: { enable_generative_enhancement: opts.enable_generative_enhancement, strict_fidelity: opts.strict_fidelity },
        metadata: { created_from: mode === "lab" ? "listing_ai_lab" : "magicad_portal", title: given },
      };
      const r = await api.post("/v1/jobs", body, { headers: { "Idempotency-Key": `lab-${crypto.randomUUID()}` } });
      toast.success("DigiHome jobber med bildene dine");
      nav(paths.job(r.data.job_id));
    } catch (e) { toast.error(errorMessage(e)); } finally { setRunning(false); }
  };

  const totalImages = (imported?.images?.length || 0) + files.length;
  const reset = () => { setImported(null); setFiles([]); setUrl(""); setName(""); setRentalChoice(null); setFurnishingChoice(undefined); setStylingLevel("recommended"); };
  const sl = imported?.source_listing || {};
  const facts = [sl.property_type ? TYPE_NB[sl.property_type] || sl.property_type : null, sl.bedrooms != null ? `${sl.bedrooms} soverom` : null, sl.area_sqm ? `${sl.area_sqm} m²` : null].filter(Boolean);
  const derivedFurnishing = imported ? deriveFurnishing(sl.title, sl.description, JSON.stringify(sl.key_facts || {})) : null;
  const furnishing = furnishingChoice === undefined ? derivedFurnishing : furnishingChoice;
  const ocOptions = config?.owner_choices;
  const levelHint = (ocOptions?.styling_level || []).find((o) => o.value === stylingLevel)?.hint?.["nb-NO"];
  const step = imported || files.length ? 2 : 1;

  return (
    <div className="mx-auto w-full max-w-[1100px] px-5 py-10 sm:px-8 sm:py-14 space-y-12">
      {/* header */}
      <div className="rise-in">
        <div className="flex items-center gap-3">
          <div className="eyebrow">Ny annonse</div>
          <div className="eyebrow text-foreground/45" data-testid="home-step">Steg {step} av 2</div>
        </div>
        <h1 className="display mt-3 text-[44px] sm:text-[64px] max-w-[18ch]">
          {step === 1 ? <>Gjør boligen klar for annonsen<span className="dot">.</span></> : <>Sjekk at vi har forstått boligen riktig<span className="dot">.</span></>}
        </h1>
        <p className="mt-4 max-w-[52ch] text-[17px] leading-[1.5] text-dim">
          {step === 1
            ? "Lim inn FINN- eller Airbnb-annonsen, eller last opp bildene. DigiHome optimaliserer, styler og setter sammen et troverdig galleri – du bestemmer til slutt."
            : "To fakta bare du vet – resten tar boligstylisten seg av."}
        </p>
      </div>

      {step === 1 ? (
        <section className="space-y-4 rise-in rise-in-1" data-testid="import-card">
          {/* one action object: link or upload */}
          <div className="panel p-1.5 flex flex-col sm:flex-row sm:items-center gap-1.5">
            <div className="relative flex-1 min-w-0">
              <Link2 className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-foreground/40" />
              <input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doImport()} placeholder="Lim inn FINN- eller Airbnb-lenke" aria-label="Annonselenke"
                className="h-12 w-full bg-transparent pl-11 pr-20 text-[15.5px] text-foreground placeholder:text-foreground/45 focus:outline-none" data-testid="import-url-input" />
              {url && <span className="absolute right-3 top-1/2 -translate-y-1/2 rounded-[6px] bg-secondary px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-foreground/60" data-testid="import-source-detected">{source === "finn" ? "FINN" : source === "airbnb" ? "Airbnb" : "Lenke"}</span>}
            </div>
            <div className="hidden sm:block h-7 w-px bg-foreground/10" />
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" onClick={() => fileRef.current?.click()} className="flex-1 sm:flex-none text-foreground/80" data-testid="mode-upload"><Upload className="h-4 w-4" />Last opp bilder</Button>
              <Button onClick={() => doImport()} disabled={importing || !url.trim()} className="min-w-[124px]" data-testid="import-fetch-button">{importing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{importing ? "Henter…" : "Hent bilder"}{!importing && <ArrowRight className="h-4 w-4" />}</Button>
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} data-testid="upload-input" />

          {/* the drop zone is the whole card; a light one under the pill so it is obvious */}
          <button type="button" onClick={() => fileRef.current?.click()} onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={(e) => { e.preventDefault(); setDragging(false); onFiles(e.dataTransfer.files); }}
            className={cn("w-full rounded-[20px] border-2 border-dashed p-8 sm:p-10 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", dragging ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent-soft))]" : "hair-strong hover:border-foreground/40 hover:bg-card/60")} data-testid="upload-dropzone">
            <ImagePlus className="h-6 w-6 mx-auto text-foreground/50" />
            <div className="mt-3 text-[15px] font-medium">Slipp bildene her, eller klikk for å velge</div>
            <div className="mt-1 text-[13px] text-svak">JPEG, PNG, WEBP eller HEIC · opptil 25 bilder · originalene lagres uendret</div>
          </button>

          <div className="flex items-start gap-2.5 rounded-[12px] bg-secondary/70 px-4 py-3">
            <Checkbox id="rights" checked={rights} onCheckedChange={(v) => setRights(Boolean(v))} data-testid="rights-checkbox" className="mt-0.5" />
            <Label htmlFor="rights" className="text-[14px] font-normal leading-5 text-foreground/80">Jeg har rett til å bruke bildene og teksten i denne annonsen (eier eller fullmakt).</Label>
          </div>
        </section>
      ) : (
        <section className="space-y-5 rise-in">
          {/* what we found */}
          <div className="panel overflow-hidden" data-testid="import-preview">
            <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="min-w-0 flex-1">
                <div className="eyebrow">{imported ? (imported.source === "finn" ? "Fra FINN" : imported.source === "airbnb" ? "Fra Airbnb" : "Fra annonsen") : "Opplastet"}</div>
                <h2 className="font-heading text-[24px] sm:text-[28px] leading-tight tracking-[-0.02em] truncate mt-1.5" data-testid="import-title">{sl.title || name.trim() || (files.length ? `${files.length} bilder lastet opp` : "Bilder")}</h2>
                <div className="mt-1.5 text-[14px] text-dim flex flex-wrap gap-x-3 gap-y-1">
                  {facts.map((f) => <span key={f}>{f}</span>)}
                  {sl.address && <span className="truncate">{sl.address}</span>}
                  <span className="font-medium text-foreground" data-testid="import-image-count">{totalImages} bilder</span>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={reset} data-testid="import-reset"><ArrowLeft className="h-3.5 w-3.5" />Start på nytt</Button>
            </div>
            <div className="px-5 sm:px-6 pb-5 sm:pb-6 space-y-4">
              <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
                {(imported?.images || []).map((im) => (
                  <div key={im.external_image_id} className="rounded-[10px] overflow-hidden image-canvas aspect-[4/3]"><SmartImage src={im.preview_url} fallbacks={[im.url, im.source_url]} alt="" /></div>
                ))}
                {previews.map((p, i) => (
                  <div key={p.url} className="relative rounded-[10px] overflow-hidden image-canvas aspect-[4/3] group">
                    <LocalPreview url={p.url} name={p.name} />
                    <button type="button" onClick={() => setFiles((f) => f.filter((_, j) => j !== i))} className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity" aria-label="Fjern" data-testid={`upload-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <button type="button" onClick={() => fileRef.current?.click()} className="rounded-[10px] border-2 border-dashed hair-strong aspect-[4/3] flex flex-col items-center justify-center text-foreground/55 hover:border-foreground/40 hover:text-foreground transition-colors text-xs gap-1" data-testid="upload-more">
                  <ImagePlus className="h-4 w-4" />Legg til
                </button>
              </div>
              {!imported && (
                <div className="relative max-w-[520px]">
                  <MapPin className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground/40" />
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Adresse eller område – brukes i annonseteksten (valgfritt)" aria-label="Adresse eller område" className="pl-10" data-testid="job-name-input" />
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} data-testid="upload-input" />
            </div>
          </div>

          {/* rental type */}
          <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Utleietype" data-testid="rental-type-choice">
            {[
              { value: "long_term", label: "Langtidsleie", Icon: Home, text: "Troverdig og nøytralt. Tomme rom kan vises møblert – alltid merket." },
              { value: "short_term", label: "Korttidsleie", Icon: Building2, text: "Gjesteklart og innbydende. Vi vurderer om boligen er klar for gjester." },
            ].map((o) => {
              const active = rentalType === o.value;
              return (
                <button key={o.value} type="button" role="radio" aria-checked={active} onClick={() => setRentalChoice(o.value)}
                  className={cn("rounded-[20px] bg-card p-5 text-left transition-[box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    active ? "shadow-[0_0_0_2px_hsl(var(--foreground))]" : "ring-hair hover:shadow-[0_0_0_1px_hsl(var(--foreground)/0.3)]")} data-testid={`rental-type-${o.value}`}>
                  <div className="flex items-start gap-3.5">
                    <div className={cn("h-10 w-10 rounded-[12px] flex items-center justify-center shrink-0", active ? "bg-[hsl(var(--accent))] text-[hsl(var(--ink))]" : "bg-secondary text-foreground")}><o.Icon className="h-4 w-4" /></div>
                    <div className="min-w-0">
                      <div className="text-[16px] font-medium">{o.label}</div>
                      <div className="mt-0.5 text-[14px] text-dim leading-[1.45]">{o.text}</div>
                    </div>
                    {active && <Check className="h-4 w-4 ml-auto shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* the owner's framing facts */}
          <div className="panel p-5 sm:p-6 space-y-5" data-testid="owner-choices">
            <div>
              <h2 className="font-heading text-[22px] tracking-[-0.02em] leading-tight">Om boligen</h2>
              <p className="mt-1 text-[14.5px] text-dim leading-[1.5]">To ting bare du vet. Resten – sengetøy, puter, lamper, hva som ryddes og hva som møbleres – bestemmer DigiHomes boligstylist ut fra hvert rom.</p>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <div className="space-y-2">
                <div className="text-[14px] font-medium">Hva følger med boligen?</div>
                <Seg value={furnishing} onChange={setFurnishingChoice} ariaLabel="Møblering" testidPrefix="furnishing"
                  options={(ocOptions?.furnishing || []).map((o) => ({ value: o.value, label: o.label["nb-NO"] }))} />
                <p className="text-[12.5px] text-svak" data-testid="furnishing-hint">
                  {furnishing ? (furnishingChoice === undefined && derivedFurnishing ? "Lest fra annonsen – endre hvis det ikke stemmer." : "Brukes i annonseteksten og av stylisten.") : "Ikke oppgitt – DigiHome spør eier, og møbler på bildene omtales ikke som inkludert."}
                </p>
              </div>
              <div className="space-y-2">
                <div className="text-[14px] font-medium">Stylingnivå</div>
                <Seg value={stylingLevel} onChange={setStylingLevel} ariaLabel="Stylingnivå" testidPrefix="styling-level"
                  options={(ocOptions?.styling_level || []).map((o) => ({ value: o.value, label: o.label["nb-NO"] }))} />
                <p className="text-[12.5px] text-svak" data-testid="styling-level-hint">
                  {levelHint || ""}{rentalType === "short_term" && stylingLevel !== "none" ? " Korttid: gjesteklart – tomme rom møbleres ikke virtuelt." : ""}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
            <Button size="lg" onClick={run} disabled={running || !totalImages} data-testid="new-job-run-button">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}{running ? "Starter…" : "Lag annonsebilder"}{!running && <ArrowRight className="h-4 w-4" />}
            </Button>
            <p className="text-[13px] text-dim leading-[1.5] flex flex-wrap items-center gap-x-1.5 gap-y-1">Tar vanligvis 1–2 minutter. Du får tilbake <TreatmentBadge kind="optimize_only" size="xs" /><TreatmentBadge kind="soft_style" size="xs" /><TreatmentBadge kind="virtual_stage" size="xs" /> – alle tydelig merket.</p>
          </div>
        </section>
      )}

      {/* what happens */}
      {step === 1 && (
        <section className="grid gap-px overflow-hidden rounded-[20px] ring-hair bg-foreground/[0.06] sm:grid-cols-3 rise-in rise-in-2" data-testid="home-how">
          {[
            { kind: "optimize_only", text: "Lys, farge, rette vertikaler og utsnitt – uten å endre boligen. Hvert bilde kontrolleres mot originalen." },
            { kind: "soft_style", text: "Møblerte rom med potensial får en stylists hånd: seng, puter, tekstiler, ryddige flater. Samme møbler, samme plass." },
            { kind: "virtual_stage", text: "Tomme rom møbleres slik en leietaker faktisk kan bo der. Alltid merket, aldri hovedbilde." },
          ].map((c) => (
            <div key={c.kind} className="bg-card p-5 sm:p-6 space-y-3">
              <TreatmentBadge kind={c.kind} />
              <p className="text-[14.5px] text-dim leading-[1.5]">{c.text}</p>
            </div>
          ))}
          <div className="bg-card p-5 sm:p-6 sm:col-span-3 flex items-center gap-2.5 text-[13px] text-svak"><ShieldCheck className="h-4 w-4 shrink-0" />Originalene lagres uendret. Ingenting publiseres uten at du sier ja.</div>
        </section>
      )}

      {/* recent */}
      {recent.length > 0 && step === 1 && (
        <section className="space-y-4" data-testid="recent-jobs">
          <div className="flex items-center justify-between">
            <h2 className="eyebrow">Siste annonser</h2>
            <Button variant="link" size="sm" className="text-[13px]" onClick={() => nav(paths.jobs)} data-testid="recent-jobs-all">Se alle<ArrowRight className="h-3 w-3" /></Button>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {recent.map((j) => (
              <button key={j.job_id} type="button" onClick={() => nav(paths.job(j.job_id))} className="rounded-[14px] bg-card ring-hair p-4 text-left hover:shadow-[0_0_0_1px_hsl(var(--foreground)/0.3)] transition-[box-shadow]" data-testid={`recent-job-${j.job_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[14.5px] font-medium truncate">{jobTitle(j, { technical: mode === "lab" })}</span>
                  <StatusBadge status={j.status} />
                </div>
                <div className="mt-1 text-[12.5px] text-dim flex gap-2"><span>{j.images_count ?? j.image_count ?? j.images_total ?? "–"} bilder</span><span>·</span><span>{fmtDate(j.created_at)}</span></div>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
